/**
 * The shadow-DOM shell for the profile bundle, copied from serve-finder so
 * the widgets stay independent. One place owns the parts that are easy to
 * get subtly wrong: the `:host` Tailwind variable block, the `:root`/`body` rewrites of
 * globals.css, the body-level overlay host for sheets (see index.tsx for why),
 * the Montserrat link in the document head and the Radix scroll-lock guard.
 *
 * The bundle mounts into `#profile-app` on purpose: every `body` rule
 * in globals.css is rewritten to that id.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { PortalContainerProvider } from '@church/nextjs-ui/bottom-sheet';
import styleText from '../src/app/globals.css?inline';

export const APP_ID = 'profile-app';
// Above the theme's fixed header (#header-outer is z-index 9999 on woodsidebible.org)
const OVERLAY_Z_INDEX = '2147483000';

export interface ShellOptions {
  /** Mount candidates, first match wins */
  containerIds: string[];
  overlayHostId: string;
  fontId: string;
  scrollLockFlag: string;
  /** Prefix for console messages */
  label: string;
  /** A class for BOTH shadow hosts (the widget's and the overlay's), so `:host(.x)` rules reach the sheets too */
  hostClass?: string;
  /**
   * The widget is a control inside a line of text (the header's login
   * trigger), not a page block: the host sizes to its content and paints no
   * background. Default false, a full-width block.
   */
  inline?: boolean;
  render: (container: HTMLElement, portalContainer: HTMLElement) => React.ReactNode;
}

export class ShadowWidget {
  private root: ReturnType<typeof createRoot> | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private overlayHost: HTMLElement | null = null;
  constructor(private opts: ShellOptions) {}

  private findContainer(): HTMLElement | null {
    for (const id of this.opts.containerIds) {
      const el = document.getElementById(id);
      if (el) return el;
    }
    return null;
  }

  init() {
    const container = this.findContainer();

    if (!container) {
      console.error(
        `${this.opts.label}: no container found (expected #${this.opts.containerIds.join(' or #')})`
      );
      return;
    }

    // Re-use existing shadow root if the widget was already initialized (back/forward nav)
    if (container.shadowRoot) {
      this.shadowRoot = container.shadowRoot;
      if (this.shadowRoot.getElementById(APP_ID)) {
        console.log(`${this.opts.label}: already initialized, skipping`);
        return;
      }
    } else {
      this.shadowRoot = container.attachShadow({ mode: 'open' });
    }

    const mountPoint = document.createElement('div');
    mountPoint.id = APP_ID;
    this.shadowRoot.appendChild(mountPoint);

    // The sheets portal into a SECOND shadow root that hangs off document.body,
    // not into this one. On woodsidebible.org the widget sits inside
    // .container-wrap and a WPBakery column, both `position: relative;
    // z-index: 10`, so a fixed overlay rendered in here is trapped in that
    // stacking context and the theme's fixed header (z-index 9999) and page
    // title band (z-index 1000) paint over the backdrop and the modal. The
    // overlay host is a zero-size positioned element with a top-level z-index,
    // so its fixed descendants stack against the page root, and it gets the
    // same stylesheet so the sheets look identical.
    const portalContainer = this.mountOverlayHost();
    if (this.opts.hostClass) {
      container.classList.add(this.opts.hostClass);
      this.overlayHost?.classList.add(this.opts.hostClass);
    }

    this.injectFont();
    const styleText = this.buildStyles();
    this.appendStyle(this.shadowRoot, styleText);
    this.appendStyle(portalContainer.getRootNode() as ShadowRoot, styleText);
    this.preventScrollLock();

    this.root = createRoot(mountPoint);
    this.root.render(
      <React.StrictMode>
        <PortalContainerProvider container={portalContainer}>
          {this.opts.render(container, portalContainer)}
        </PortalContainerProvider>
      </React.StrictMode>
    );
  }

  /**
   * Prevent Radix UI scroll locking from causing layout shift on the host page.
   * Radix adds data-scroll-locked, overflow:hidden, and paddingRight to body/html
   * when a Dialog/Sheet opens. Since our sheet lives in the shadow DOM, the host
   * page should not be affected.
   */
  private preventScrollLock() {
    if ((window as any)[this.opts.scrollLockFlag]) return;

    const cleanElement = (el: HTMLElement) => {
      if (el.hasAttribute('data-scroll-locked')) {
        el.removeAttribute('data-scroll-locked');
      }
      // Only remove Radix's specific inline styles, not pre-existing ones
      if (el.style.getPropertyValue('overflow') === 'hidden') {
        el.style.removeProperty('overflow');
      }
      if (el.style.getPropertyValue('padding-right')) {
        el.style.removeProperty('padding-right');
      }
      if (el.style.getPropertyValue('margin-right')) {
        el.style.removeProperty('margin-right');
      }
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-scroll-locked') {
          cleanElement(document.body);
          cleanElement(document.documentElement);
        }
      }
    });

    observer.observe(document.body, { attributes: true, attributeFilter: ['data-scroll-locked'] });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-scroll-locked'],
    });

    (window as any)[this.opts.scrollLockFlag] = true;
  }

  private injectFont() {
    // Google Fonts must be loaded in the document head, not inside shadow DOM
    const fontId = this.opts.fontId;
    if (!document.getElementById(fontId)) {
      const link = document.createElement('link');
      link.id = fontId;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&family=Bebas+Neue&family=Pirata+One&display=swap';
      document.head.appendChild(link);
    }
  }

  /**
   * The overlay host: a zero-size element at the end of <body> with its own
   * shadow root, positioned with a top-level z-index so fixed overlays inside
   * it stack against the page rather than inside the widget's ancestors.
   * The portal container inside it reuses APP_ID so every `#profile-app`
   * rule (font, transparent background) applies there too.
   */
  private mountOverlayHost(): HTMLElement {
    let host = document.getElementById(this.opts.overlayHostId);
    if (!host) {
      host = document.createElement('div');
      host.id = this.opts.overlayHostId;
      host.style.cssText = `position:relative;z-index:${OVERLAY_Z_INDEX};width:0;height:0;`;
      document.body.appendChild(host);
    }
    // Several widgets can share one host (login.js mounts once per login menu
    // item, three times on woodsidebible.org). Reuse the container: rebuilding
    // it here detached the first widget's portal target, so its drawer
    // rendered into a node no longer in the document and never showed.
    const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    let container = root.getElementById(APP_ID) as HTMLElement | null;
    if (!container) {
      container = document.createElement('div');
      container.id = APP_ID;
      root.appendChild(container);
    }
    this.overlayHost = host;
    return container;
  }

  private buildStyles(): string {
    const resetCSS = `
      *, *::before, *::after { box-sizing: border-box; }

      :host { display: ${this.opts.inline ? 'inline-block' : 'block'}; width: ${this.opts.inline ? 'auto' : '100%'}; vertical-align: middle; }

      #${APP_ID} {
        width: ${this.opts.inline ? 'auto' : '100%'};
        min-width: ${this.opts.inline ? '0' : '100%'};
        max-width: 100%;
        ${this.opts.inline ? 'background: transparent !important; display: inline-block; line-height: normal;' : ''}
        font-family: 'Montserrat', ui-sans-serif, system-ui, sans-serif;
      }

      :host {
        --tw-border-spacing-x: 0;
        --tw-border-spacing-y: 0;
        --tw-translate-x: 0;
        --tw-translate-y: 0;
        --tw-rotate: 0;
        --tw-skew-x: 0;
        --tw-skew-y: 0;
        --tw-scale-x: 1;
        --tw-scale-y: 1;
        --tw-pan-x: ;
        --tw-pan-y: ;
        --tw-pinch-zoom: ;
        --tw-scroll-snap-strictness: proximity;
        --tw-gradient-from-position: ;
        --tw-gradient-via-position: ;
        --tw-gradient-to-position: ;
        --tw-ordinal: ;
        --tw-slashed-zero: ;
        --tw-numeric-figure: ;
        --tw-numeric-spacing: ;
        --tw-numeric-fraction: ;
        --tw-ring-inset: ;
        --tw-ring-offset-width: 0px;
        --tw-ring-offset-color: #fff;
        --tw-ring-color: rgb(59 130 246 / 0.5);
        --tw-ring-offset-shadow: 0 0 #0000;
        --tw-ring-shadow: 0 0 #0000;
        --tw-shadow: 0 0 #0000;
        --tw-shadow-colored: 0 0 #0000;
        --tw-inset-shadow: 0 0 #0000;
        --tw-inset-ring-shadow: 0 0 #0000;
        --tw-border-style: solid;
        --tw-blur: ;
        --tw-brightness: ;
        --tw-contrast: ;
        --tw-grayscale: ;
        --tw-hue-rotate: ;
        --tw-invert: ;
        --tw-saturate: ;
        --tw-sepia: ;
        --tw-drop-shadow: ;
        --tw-backdrop-blur: ;
        --tw-backdrop-brightness: ;
        --tw-backdrop-contrast: ;
        --tw-backdrop-grayscale: ;
        --tw-backdrop-hue-rotate: ;
        --tw-backdrop-invert: ;
        --tw-backdrop-opacity: ;
        --tw-backdrop-saturate: ;
        --tw-backdrop-sepia: ;
        --tw-contain-size: ;
        --tw-contain-layout: ;
        --tw-contain-paint: ;
        --tw-contain-style: ;
      }
    `;

    // Replace :root -> :host and body -> #profile-app for shadow DOM
    const shadowCSS = styleText
      .replace(/:root\b/g, ':host')
      .replace(/\bbody\b/g, `#${APP_ID}`)
      .replace(
        new RegExp(`(#${APP_ID}\\s*{[^}]*?)overflow-y:\\s*scroll\\s*!important;?`, 'g'),
        '$1'
      );

    // The host page decides the background. globals.css paints `body` white,
    // which became the mount point after the rewrite above, so undo that.
    const transparentMount = `#${APP_ID} { background: transparent; }`;

    return resetCSS + '\n' + shadowCSS + '\n' + transparentMount;
  }

  /** One copy of the stylesheet per shadow root, however many widgets share it. */
  private appendStyle(root: ShadowRoot, css: string) {
    if (root.querySelector(`style[data-shell="${this.opts.label}"]`)) return;
    const style = document.createElement('style');
    style.dataset.shell = this.opts.label;
    style.textContent = css;
    root.appendChild(style);
  }

  destroy() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
    if (this.overlayHost) {
      this.overlayHost.remove();
      this.overlayHost = null;
    }
  }
}
